import React, { JSX, useEffect, useRef, useState } from 'react';
import {
  Controller,
  FieldErrors,
  FieldPath,
  UseFormReturn,
} from 'react-hook-form';

import { Plus, SparklesIcon } from 'lucide-react';

import { Checkbox } from 'components/Checkbox';
import { RHFLocationSelect } from 'components/input/RHFLocationSelect';
import { ReferenceNumberInput } from 'components/input/ReferenceNumberInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { getLocationByID } from 'lib/api/getLocationByID';
import { getLocationDefaultHours } from 'lib/api/getLocationDefaultHours';
import {
  LoadDateInput,
  LoadDateTimeInput,
} from 'pages/LoadView/LoadInformation/Components';
import { LoadStopTypes, NormalizedLoad, TMSLocation } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import LocationCreatorDialog from '../components/LocationCreatorDialog';
import { formatTimezoneForDisplay } from './utils/threeGUtils';

const SUPPORTS_LOCATION_CREATION = true;

export function StopForm({
  stop,
  formMethods,
  isLoadingLocations,
  locations,
  handleRefreshLocations,
  originalSuggestionData,
  tmsID,
  setLocations,
  customerID,
  customerExternalTmsId,
  aiHighlightedExternalTMSID,
  isCreateMode = false,
}: {
  stop: LoadStopTypes;
  formMethods: UseFormReturn<NormalizedLoad>;
  isLoadingLocations: boolean;
  locations: Maybe<TMSLocation[]>;
  handleRefreshLocations: () => void;
  originalSuggestionData?: NormalizedLoad;
  tmsID: number;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
  customerID?: number;
  /** When set, location search is filtered to this customer (e.g. 3G). Sent when customerID is not available. */
  customerExternalTmsId?: string;
  aiHighlightedExternalTMSID?: string; // If provided, the matching option will be highlighted with AI sparkle icon
  isCreateMode?: boolean;
}): JSX.Element {
  const [isLocationCreatorOpen, setIsLocationCreatorOpen] = useState(false);
  const [aiSuggestedLocationData, setAiSuggestedLocationData] = useState<
    Maybe<{
      name: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      zipcode: string;
      country: string;
      phone: string;
      email: string;
      specialInstructions: string;
    }>
  >(null);
  const {
    control,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = formMethods;

  const watchedLocationID = watch(`${stop}.externalTMSID`);
  const watchedLocObj = watch(stop);
  const watchedMode = watch('mode');
  const isLTLMode = watchedMode === 'LTL';
  const watchedDate = watch(
    stop === 'pickup'
      ? ('pickup.readyTime' as FieldPath<NormalizedLoad>)
      : ('consignee.mustDeliver' as FieldPath<NormalizedLoad>)
  );
  const watchedLoad = getValues();
  const watchedCustomer = watch('customer');

  const resolveBestLocation = (cands: TMSLocation[]): Undef<TMSLocation> => {
    if (cands.length === 0) return undefined;
    const fromApi = (loc: TMSLocation) =>
      loc.ID != null && typeof loc.ID === 'number';
    const apiCands = cands.filter(fromApi);
    if (apiCands.length > 0) {
      return apiCands.reduce((best, loc) =>
        ((loc.ID ?? 0) as number) > ((best.ID ?? 0) as number) ? loc : best
      );
    }
    return cands.find((loc) => !!loc.addressLine1) ?? cands[0];
  };

  const lastSyncedIDRef = useRef<string | undefined>(undefined);
  const defaultHoursFetchedRef = useRef<string>('');
  const prevLocationForHoursRef = useRef<string | undefined>(undefined);
  type DefaultHoursStatus = 'idle' | 'loading' | 'loaded' | 'empty';
  const [defaultHoursStatus, setDefaultHoursStatus] =
    useState<DefaultHoursStatus>('idle');

  useEffect(() => {
    if (stop === 'consignee' && isLTLMode) {
      clearErrors('consignee.mustDeliver');
    }
  }, [clearErrors, isLTLMode, stop]);

  const dateStr = (() => {
    const v = watchedDate;
    if (v == null) return '';
    if (v instanceof Date && !isNaN(v.getTime()))
      return v.toISOString().slice(0, 10);
    if (typeof v === 'string') return v.includes('T') ? v.slice(0, 10) : v;
    return '';
  })();

  // Fetch default hours only when the user has selected or changed the location and we don't have hours for it.
  useEffect(() => {
    if (!watchedLocationID || !dateStr || !tmsID) {
      defaultHoursFetchedRef.current = '';
      prevLocationForHoursRef.current = undefined;
      setDefaultHoursStatus('idle');
      return;
    }
    // User changed location: clear hours so we fetch for the new location.
    const locationChanged =
      prevLocationForHoursRef.current !== undefined &&
      prevLocationForHoursRef.current !== watchedLocationID;
    if (locationChanged) {
      setValue(`${stop}.openTime` as FieldPath<NormalizedLoad>, '', {
        shouldDirty: true,
      });
      setValue(`${stop}.closeTime` as FieldPath<NormalizedLoad>, '', {
        shouldDirty: true,
      });
      defaultHoursFetchedRef.current = '';
    }
    prevLocationForHoursRef.current = watchedLocationID;

    const cacheKey = `${watchedLocationID}:${dateStr}`;
    // If we already have hours for this exact location+date (from suggestion or prior fetch), skip the API call.
    // Must check cacheKey so that changing the date triggers a re-fetch (dock hours can differ by day).
    const hasHours = watchedLocObj?.openTime && watchedLocObj?.closeTime;
    if (
      hasHours &&
      !locationChanged &&
      defaultHoursFetchedRef.current === cacheKey
    ) {
      setDefaultHoursStatus('loaded');
      return;
    }
    if (cacheKey === defaultHoursFetchedRef.current) {
      return;
    }
    defaultHoursFetchedRef.current = cacheKey;
    setDefaultHoursStatus('loading');

    const originOrDest = stop === 'pickup' ? 'origin' : 'dest';
    getLocationDefaultHours({
      tmsID,
      locationExternalId: watchedLocationID,
      date: dateStr,
      originOrDest,
    }).then((result) => {
      // Ignore stale response if user changed location/date before this resolved
      if (defaultHoursFetchedRef.current !== cacheKey) {
        return;
      }
      if (result.isErr()) {
        // Don't show 'empty' if location-sync already populated hours after we started the fetch
        const formValues = getValues();
        const stopData =
          stop === 'pickup' ? formValues?.pickup : formValues?.consignee;
        if (stopData?.openTime && stopData?.closeTime) {
          setDefaultHoursStatus('loaded');
        } else {
          setDefaultHoursStatus('empty');
        }
        return;
      }
      const { openTime, closeTime, timezone } = result.value;
      if (openTime || closeTime) {
        setValue(
          `${stop}.openTime` as FieldPath<NormalizedLoad>,
          openTime ?? '',
          { shouldDirty: true }
        );
        setValue(
          `${stop}.closeTime` as FieldPath<NormalizedLoad>,
          closeTime ?? '',
          { shouldDirty: true }
        );
        setDefaultHoursStatus('loaded');
      } else {
        // API returned no hours; don't show 'empty' if location-sync already populated hours
        const formValues = getValues();
        const stopData =
          stop === 'pickup' ? formValues?.pickup : formValues?.consignee;
        if (stopData?.openTime && stopData?.closeTime) {
          setDefaultHoursStatus('loaded');
        } else {
          setDefaultHoursStatus('empty');
        }
      }
      if (timezone) {
        setValue(`${stop}.timezone` as FieldPath<NormalizedLoad>, timezone, {
          shouldDirty: true,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedLocationID,
    dateStr,
    tmsID,
    stop,
    setValue,
    watchedLocObj?.openTime,
    watchedLocObj?.closeTime,
  ]);

  // 3G: when user selects a location with threeGLocationId, fetch default contact from TMS and set on form
  const defaultContactFetchedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!watchedLocationID || !tmsID) {
      defaultContactFetchedRef.current = undefined;
      return;
    }
    const cands =
      locations?.filter((loc) => loc.externalTMSID === watchedLocationID) ?? [];
    const selectedLoc = resolveBestLocation(cands);
    const locID = selectedLoc?.threeGLocationId;
    if (locID == null || String(locID) === defaultContactFetchedRef.current) {
      return;
    }
    defaultContactFetchedRef.current = String(locID);
    getLocationByID(tmsID, String(locID)).then((result) => {
      if (
        result.isErr() ||
        defaultContactFetchedRef.current !== String(locID)
      ) {
        return;
      }
      const { location } = result.value;
      if (location.contact || location.phone || location.email) {
        setValue(
          `${stop}.contact` as FieldPath<NormalizedLoad>,
          location.contact ?? '',
          { shouldDirty: true }
        );
        setValue(
          `${stop}.phone` as FieldPath<NormalizedLoad>,
          location.phone ?? '',
          { shouldDirty: true }
        );
        setValue(
          `${stop}.email` as FieldPath<NormalizedLoad>,
          location.email ?? '',
          { shouldDirty: true }
        );
      }
    });
  }, [watchedLocationID, locations, tmsID, stop, setValue]);

  useEffect(() => {
    const cands =
      locations?.filter((loc) => loc.externalTMSID === watchedLocationID) ?? [];
    const selectedLoc = resolveBestLocation(cands);
    const idChanged =
      watchedLocationID && watchedLocationID !== lastSyncedIDRef.current;
    const locationHasMoreData =
      selectedLoc?.addressLine1 && !watchedLocObj?.addressLine1;
    const locationDataMismatch =
      selectedLoc &&
      watchedLocObj &&
      ((selectedLoc.name != null && selectedLoc.name !== watchedLocObj.name) ||
        (selectedLoc.addressLine1 != null &&
          selectedLoc.addressLine1 !== watchedLocObj.addressLine1));

    if (
      watchedLocationID &&
      selectedLoc &&
      (idChanged || locationHasMoreData || locationDataMismatch)
    ) {
      Object.entries(selectedLoc).forEach(([key, value]) => {
        setValue(`${stop}.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: true,
        });
      });
      // Always update apptRequired when location changes; default to false when undefined
      // (locations without isPickupAptRequired/isDeliveryAptRequired would otherwise leave stale value)
      const apptRequiredFromApi =
        stop === 'pickup'
          ? selectedLoc.isPickupAptRequired
          : selectedLoc.isDeliveryAptRequired;
      setValue(
        `${stop}.apptRequired` as FieldPath<NormalizedLoad>,
        apptRequiredFromApi ?? false,
        {
          shouldDirty: true,
        }
      );

      lastSyncedIDRef.current = watchedLocationID;
    } else if (!watchedLocationID) {
      lastSyncedIDRef.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedLocationID,
    locations,
    setValue,
    stop,
    watchedLocObj?.addressLine1,
    watchedLocObj?.name,
  ]);

  // Location Sync: Clear externalTMSID if user manually edits address fields after selection
  useEffect(() => {
    if (watchedLocationID) {
      const cands =
        locations?.filter((loc) => loc.externalTMSID === watchedLocationID) ??
        [];
      const selectedLoc = resolveBestLocation(cands);
      if (!selectedLoc) {
        return;
      }

      if (
        (selectedLoc?.name &&
          watchedLocObj?.name.toLowerCase() !==
            selectedLoc?.name.toLowerCase()) ||
        (selectedLoc?.addressLine1 &&
          watchedLocObj?.addressLine1.toLowerCase() !==
            selectedLoc?.addressLine1.toLowerCase()) ||
        (selectedLoc?.addressLine2 &&
          watchedLocObj?.addressLine2?.toLowerCase() !==
            selectedLoc?.addressLine2?.toLowerCase()) ||
        (selectedLoc?.city &&
          watchedLocObj?.city?.toLowerCase() !==
            selectedLoc?.city?.toLowerCase()) ||
        (selectedLoc?.state &&
          watchedLocObj?.state?.toLowerCase() !==
            selectedLoc?.state?.toLowerCase()) ||
        (selectedLoc?.zipCode &&
          watchedLocObj?.zipCode?.toLowerCase() !==
            selectedLoc?.zipCode?.toLowerCase())
      ) {
        setValue(`${stop}.externalTMSID` as FieldPath<NormalizedLoad>, '');
      }
    }
  }, [
    watchedLocationID,
    locations,
    watchedLocObj?.name,
    watchedLocObj?.addressLine1,
    watchedLocObj?.addressLine2,
    watchedLocObj?.city,
    watchedLocObj?.state,
    watchedLocObj?.zipCode,
    setValue,
    stop,
  ]);

  useEffect(() => {
    const {
      apptType: currentApptType,
      apptStartTime,
      apptEndTime,
    } = watchedLocObj ?? {};

    if ((apptStartTime || apptEndTime) && !currentApptType) {
      setValue(
        `${stop}.apptType` as FieldPath<NormalizedLoad>,
        'By appointment',
        {
          shouldDirty: true,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedLocObj?.apptStartTime,
    watchedLocObj?.apptEndTime,
    watchedLocObj?.apptType,
    setValue,
    stop,
  ]);

  useEffect(() => {
    const startPath = `${stop}.apptStartTime` as FieldPath<NormalizedLoad>;
    const hasEndTime = !!watchedLocObj?.apptEndTime;

    if (hasEndTime) {
      formMethods.trigger(startPath);
    } else {
      formMethods.clearErrors(startPath);
    }
  }, [watchedLocObj?.apptEndTime, formMethods, stop]);

  // Detect AI-suggested location without externalTMSID and prepare data for pre-population
  useEffect(() => {
    if (!SUPPORTS_LOCATION_CREATION) return;

    if (
      originalSuggestionData?.[stop] &&
      !originalSuggestionData[stop].externalTMSID &&
      originalSuggestionData[stop].name &&
      originalSuggestionData[stop].addressLine1 &&
      originalSuggestionData[stop].city &&
      originalSuggestionData[stop].state &&
      originalSuggestionData[stop].zipCode
    ) {
      const loc = originalSuggestionData[stop];
      setAiSuggestedLocationData({
        name: loc.name || '',
        addressLine1: loc.addressLine1 || '',
        addressLine2: loc.addressLine2 || '',
        city: loc.city || '',
        state: loc.state || '',
        zipcode: loc.zipCode || '',
        country: loc.country || 'United States',
        phone: loc.phone || '',
        email: loc.email || '',
        specialInstructions: loc.instructions || '',
      });
    } else {
      setAiSuggestedLocationData(null);
    }
  }, [originalSuggestionData, stop]);

  const handleLocationCreated = (newLocation: TMSLocation) => {
    const updatedLocations = [...(locations || []), newLocation];
    setLocations(updatedLocations as Maybe<TMSLocation[]>);

    setValue(
      `${stop}.externalTMSID` as FieldPath<NormalizedLoad>,
      newLocation.externalTMSID,
      {
        shouldDirty: true,
      }
    );
  };

  const hasAiSuggestedNewLocation =
    SUPPORTS_LOCATION_CREATION &&
    originalSuggestionData?.[stop] &&
    !originalSuggestionData[stop].externalTMSID &&
    originalSuggestionData[stop].name &&
    originalSuggestionData[stop].addressLine1 &&
    originalSuggestionData[stop].city &&
    originalSuggestionData[stop].state &&
    originalSuggestionData[stop].zipCode;

  return (
    <>
      {hasAiSuggestedNewLocation && (
        <div className='rounded border-l-4 border-accent-500 bg-accent-50 p-3 mb-1'>
          <Flex align='center' gap='xs'>
            <SparklesIcon
              className='h-4 w-4 stroke-accent-600! shrink-0'
              strokeWidth={2}
            />
            <Typography variant='body-sm' className='text-neutral-700'>
              AI detected a new location,{' '}
              <span
                className='underline cursor-pointer text-accent-600 hover:text-accent-800 font-medium'
                onClick={() => setIsLocationCreatorOpen(true)}
              >
                click here to add
              </span>
            </Typography>
          </Flex>
        </div>
      )}

      <RHFLocationSelect
        required={true}
        name={`${stop}.externalTMSID`}
        label='Name'
        control={control}
        errors={errors}
        locations={locations}
        setLocations={setLocations}
        isLoadingLocations={isLoadingLocations}
        handleRefreshLocations={handleRefreshLocations}
        labelIsName={true}
        customerID={customerID}
        customerExternalTmsId={customerExternalTmsId}
        aiHighlightedExternalTMSID={aiHighlightedExternalTMSID}
        customActionButtons={
          SUPPORTS_LOCATION_CREATION ? (
            <button
              type='button'
              onClick={() => setIsLocationCreatorOpen(true)}
              className='shrink-0 py-2 px-1 hover:text-info-600 rounded-full transition-colors cursor-pointer'
              title='Create new location'
            >
              <Plus className='h-4 w-4' />
            </button>
          ) : undefined
        }
      />

      <LoadBuildingTextInput
        name={`${stop}.addressLine1`}
        label='Address Line 1'
        readOnly={true}
      />

      <LoadBuildingTextInput
        name={`${stop}.addressLine2`}
        label='Address Line 2'
        readOnly={true}
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <LoadBuildingTextInput
          name={`${stop}.city`}
          label='City'
          readOnly={true}
        />

        <LoadBuildingTextInput
          name={`${stop}.state`}
          label='State'
          readOnly={true}
        />
      </Grid>

      <LoadBuildingTextInput
        name={`${stop}.zipCode`}
        label='Zip Code'
        placeholder='12345'
        readOnly={true}
      />

      <Flex align='center' gap='sm' className='py-2'>
        <Controller
          name={`${stop}.apptRequired` as FieldPath<NormalizedLoad>}
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value === true}
              onCheckedChange={(checked) => field.onChange(checked === true)}
              disabled={true}
              label={
                stop === 'pickup' ? 'Pickup Appt Required' : 'Del Appt Required'
              }
              labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
            />
          )}
        />
      </Flex>

      {stop === 'pickup' ? (
        <LoadDateInput
          name={`${stop}.readyTime` as FieldPath<NormalizedLoad>}
          label='Pickup Date'
          required
          forceEditable
        />
      ) : (
        <LoadDateInput
          name={`${stop}.mustDeliver` as FieldPath<NormalizedLoad>}
          label='Delivery Date'
          required={!isLTLMode}
          forceEditable
        />
      )}

      {/* Read-only location working hours (from 3G getDefaultDate). Shown when location + date are set. */}
      {watchedLocationID && dateStr && (
        <div className='rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2'>
          <Typography
            variant='body-sm'
            weight='medium'
            className='text-neutral-600 mb-0.5'
          >
            Working hours
          </Typography>
          <Typography variant='body-sm' className='text-neutral-700'>
            {defaultHoursStatus === 'loading' && (
              <span className='text-neutral-500'>Loading…</span>
            )}
            {defaultHoursStatus === 'loaded' &&
              (watchedLocObj?.openTime || watchedLocObj?.closeTime) && (
                <>
                  {[watchedLocObj.openTime, watchedLocObj.closeTime]
                    .filter(Boolean)
                    .join(' – ')}
                  {watchedLocObj?.timezone && (
                    <span className='text-neutral-500 ml-1'>
                      {formatTimezoneForDisplay(watchedLocObj.timezone)}
                    </span>
                  )}
                </>
              )}
            {defaultHoursStatus === 'empty' && (
              <span className='text-neutral-500'>No hours available</span>
            )}
          </Typography>
        </div>
      )}

      {!isCreateMode && (
        <>
          <LoadDateTimeInput
            name={`${stop}.apptStartTime` as FieldPath<NormalizedLoad>}
            label='Appointment Start Time'
            load={originalSuggestionData || watchedLoad}
            defaultDate={
              stop === 'pickup'
                ? watchedLoad.pickup?.readyTime
                : watchedLoad.consignee?.mustDeliver
            }
            forceEditable
            rules={{
              required: watchedLocObj?.apptEndTime ? 'Required' : false,
            }}
          />

          <LoadDateTimeInput
            name={`${stop}.apptEndTime` as FieldPath<NormalizedLoad>}
            label='Appointment End Time'
            load={originalSuggestionData || watchedLoad}
            defaultDate={
              stop === 'pickup'
                ? watchedLoad.pickup?.readyTime
                : watchedLoad.consignee?.mustDeliver
            }
            forceEditable
          />
        </>
      )}

      <ReferenceNumberInput
        name={`${stop}.refNumber`}
        control={control}
        label=''
        placeholder=''
        load={originalSuggestionData || watchedLoad}
        className='hidden'
      />

      <LoadBuildingTextInput
        name={`${stop}.apptNum`}
        label='Appointment Number'
        placeholder='Appointment Number'
      />

      {SUPPORTS_LOCATION_CREATION && (
        <LocationCreatorDialog
          isOpen={isLocationCreatorOpen}
          onClose={() => setIsLocationCreatorOpen(false)}
          onLocationCreated={handleLocationCreated}
          tmsID={tmsID}
          stopType={stop}
          initialData={aiSuggestedLocationData || undefined}
          tradingPartnerNum={watchedCustomer?.externalTMSID}
        />
      )}
    </>
  );
}

export function hasNoErrorsInSubsection(
  subsectionPath: string,
  formErrors: FieldErrors
): boolean {
  const subsectionErrors = Object.entries(formErrors).filter(([key]) =>
    key.startsWith(subsectionPath)
  );

  return subsectionErrors.length === 0;
}
